import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { productsAPI, certificatesAPI } from '@/utils/api';
import { Upload, Download, Trash2, FileText, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import DipBreadcrumb from '@/components/DipBreadcrumb';
import DipStepFooter from '@/components/DipStepFooter';

const Certificates = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadForm, setUploadForm] = useState({ certificate_type: '', file: null });
  const [uploading, setUploading] = useState(false);

  useEffect(() => { loadData(); }, [productId]);

  const loadData = async () => {
    try {
      const prodRes = await productsAPI.getOne(productId);
      setProduct(prodRes.data);
      const certRes = await certificatesAPI.getByProduct(productId);
      setCertificates(certRes.data);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally { setLoading(false); }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) { toast.error('Sélectionnez un fichier'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('product_id', productId);
      formData.append('certificate_type', uploadForm.certificate_type || 'Certificat');
      formData.append('supplier_id', '');
      formData.append('file', uploadForm.file);
      await certificatesAPI.upload(formData);
      toast.success('Certificat ajouté');
      setUploadForm({ certificate_type: '', file: null });
      const fileInput = document.getElementById('cert-file-input');
      if (fileInput) fileInput.value = '';
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'upload");
    } finally { setUploading(false); }
  };

  const handleDownload = async (certificateId, filename) => {
    try {
      const res = await certificatesAPI.download(certificateId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
    } catch (e) { toast.error('Erreur'); }
  };

  const handleDelete = async (certificateId) => {
    if (!window.confirm('Supprimer ce certificat ?')) return;
    try {
      await certificatesAPI.delete(certificateId);
      toast.success('Certificat supprimé');
      loadData();
    } catch (e) { toast.error('Erreur'); }
  };

  if (loading) {
    return <div className="p-8"><div className="skeleton-shimmer h-64 rounded-lg"></div></div>;
  }

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <DipBreadcrumb productId={productId} productName={product?.name} currentStep="certificates" />

      <div className="mb-8">
        <h1 className="text-4xl font-semibold text-foreground mb-2 tracking-tight">Certificats</h1>
        <p className="text-base text-muted-foreground leading-relaxed">
          {product?.name} — Ajoutez tous les certificats que vous souhaitez (optionnel).
          Les fournisseurs ne sont pas nécessaires ici, ils figurent dans le rapport toxicologique.
        </p>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Ajouter un certificat</CardTitle>
          <CardDescription>Fiches techniques, certificats bio, attestations qualité, etc.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleUpload} className="space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">
                Type / nom du certificat
              </label>
              <Input
                data-testid="input-cert-type"
                placeholder="Ex: Certificat bio Cosmos, Fiche technique fournisseur, Attestation GMP..."
                value={uploadForm.certificate_type}
                onChange={(e) => setUploadForm({ ...uploadForm, certificate_type: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">
                Fichier
              </label>
              <Input
                id="cert-file-input"
                data-testid="input-cert-file"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) setUploadForm({ ...uploadForm, file });
                }}
                required
              />
              {uploadForm.file && (
                <p className="text-xs text-muted-foreground mt-1">
                  {uploadForm.file.name} ({(uploadForm.file.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </div>
            <Button data-testid="btn-upload-cert" type="submit" disabled={uploading} className="w-full gap-2">
              <Upload size={18} />
              {uploading ? 'Upload en cours...' : 'Ajouter ce certificat'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Certificats ajoutés
            {certificates.length > 0 && (
              <span className="text-xs font-normal px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                {certificates.length}
              </span>
            )}
          </CardTitle>
          <CardDescription>Vous pouvez en ajouter autant que vous le souhaitez.</CardDescription>
        </CardHeader>
        <CardContent>
          {certificates.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">Aucun certificat — l'ajout est optionnel.</p>
          ) : (
            <div className="space-y-3">
              {certificates.map((cert) => (
                <div key={cert.id} className="flex items-center justify-between p-3 border border-border rounded-md hover:border-primary/30 transition-colors">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <CheckCircle2 className="text-green-600 shrink-0" size={20} />
                    <FileText className="text-primary shrink-0" size={20} strokeWidth={1.5} />
                    <div className="min-w-0">
                      <p className="font-medium text-foreground truncate">{cert.certificate_type}</p>
                      <p className="text-sm text-muted-foreground truncate">{cert.filename}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button data-testid={`btn-download-cert-${cert.id}`} size="sm" variant="outline"
                      onClick={() => handleDownload(cert.id, cert.filename)}>
                      <Download size={16} />
                    </Button>
                    <Button data-testid={`btn-delete-cert-${cert.id}`} size="sm" variant="ghost"
                      onClick={() => handleDelete(cert.id)}
                      className="text-destructive hover:text-destructive">
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <DipStepFooter productId={productId} currentStep="certificates" nextLabel="Générer le DIP" />
    </div>
  );
};

export default Certificates;
